import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { Tooltip, IconButton, Badge } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsIcon from "@mui/icons-material/Notifications";

import styles from "./UserManagement.module.css";
import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";
import CustomDatePicker from "../../components/CustomDatePicker/CustomDatePicker";
import { AxiosInstance } from "../../services/Api";

const RequestContact = () => {
  const navigate = useNavigate();

  // ─── Data state ───
  const [contactRequests, setContactRequests] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Filter / pagination state ───
  const [columnFilters, setColumnFilters] = useState([]);
  const [apiSearch, setApiSearch] = useState(""); // ✅ committed only on Enter
  const [statusFilter, setStatusFilter] = useState(""); // "", "1" (Read), "0" (Unread)
  const [startDateFilter, setStartDateFilter] = useState(""); // YYYY-MM-DD
  const [endDateFilter, setEndDateFilter] = useState(""); // YYYY-MM-DD
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ─── Fetch contact requests (POST /roll/contact-list) ───
  const fetchContactRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: apiSearch,
        is_active: statusFilter === "" ? "" : Number(statusFilter),
        start_date: startDateFilter, // YYYY-MM-DD or ""
        end_date: endDateFilter, // YYYY-MM-DD or ""
      };

      const response = await AxiosInstance.post("/roll/contact-list", payload);

      if (response.data?.success && response.data.data) {
        setContactRequests(response.data.data.contactList || []);
        setActiveCount(response.data.active_count || 0);
        setTotalRecords(response.data.data.pagination?.total_records || 0);
      } else {
        setContactRequests([]);
        setActiveCount(0);
        setTotalRecords(0);
        throw new Error(
          response.data?.message || "Failed to fetch contact requests",
        );
      }
    } catch (err) {
      console.error("Error fetching contact requests:", err);
      setError(err.message || "An error occurred");
      setContactRequests([]);
      setActiveCount(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
    statusFilter,
    startDateFilter,
    endDateFilter,
  ]);

  useEffect(() => {
    fetchContactRequests();
  }, [fetchContactRequests]);

  // ─── View handler ───
  const handleView = useCallback(
    (row) => {
      navigate(`/dashboard/masters/contact-request/view/${row.id}`);
    },
    [navigate],
  );

  // ─── Column filter state ───
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;
    setColumnFilters(newFilters);

    // ✅ Status select commits immediately
    const statusEntry = newFilters.find((f) => f.id === "status");
    const newStatus = statusEntry?.value ?? "";
    const mappedStatus =
      newStatus === "Read" ? "1" : newStatus === "Unread" ? "0" : "";

    if (mappedStatus !== statusFilter) {
      setStatusFilter(mappedStatus);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }

    // ✅ Created Date range filter commits immediately
    const dateEntry = newFilters.find((f) => f.id === "created_at");
    const dateVal = dateEntry?.value || {};
    const newStart = dateVal.start || "";
    const newEnd = dateVal.end || "";

    if (newStart !== startDateFilter) {
      setStartDateFilter(newStart);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
    if (newEnd !== endDateFilter) {
      setEndDateFilter(newEnd);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  };

  // ─── Only Enter commits the text search → triggers API ───
  const handleFilterKeyDown = (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setApiSearch(inputValue.trim());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  };

  // ─── Date Range Filter (From / To with disable-before-from logic) ───
  const DateRangeFilter = ({ column }) => {
    const filterValue = column.getFilterValue() || {};
    const [localStart, setLocalStart] = useState(filterValue.start || "");
    const [localEnd, setLocalEnd] = useState(filterValue.end || "");

    useEffect(() => {
      setLocalStart(filterValue.start || "");
      setLocalEnd(filterValue.end || "");
    }, [filterValue.start, filterValue.end]);

    const startValue = localStart ? dayjs(localStart) : null;
    const endValue = localEnd ? dayjs(localEnd) : null;

    const handleStartChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalStart(formatted);

      // If existing To Date is before the new From Date → clear it
      let nextEnd = localEnd;
      if (
        formatted &&
        localEnd &&
        dayjs(localEnd).isBefore(dayjs(formatted), "day")
      ) {
        nextEnd = "";
        setLocalEnd("");
      }

      const nextValue =
        !formatted && !nextEnd ? undefined : { start: formatted, end: nextEnd };
      column.setFilterValue(nextValue);
    };

    const handleEndChange = (dayjsDate) => {
      const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
      setLocalEnd(formatted);
      const nextValue =
        !localStart && !formatted
          ? undefined
          : { start: localStart, end: formatted };
      column.setFilterValue(nextValue);
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          minWidth: "180px",
        }}
      >
        <CustomDatePicker
          name="start_date"
          value={startValue}
          onChange={handleStartChange}
          placeholder="From Date"
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          maxDate={endValue || undefined}
        />
        <CustomDatePicker
          name="end_date"
          value={endValue}
          onChange={handleEndChange}
          placeholder="To Date"
          format="DD/MM/YYYY"
          size="sm"
          isClearable={true}
          minDate={startValue || undefined}
          disabled={!startValue}
        />
      </div>
    );
  };

  // ─── Format date as DD/MM/YYYY HH:mm:ss (for Excel) ───
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // ─── Excel download (current page data only) ───
  const handleExcelDownload = async () => {
    try {
      if (!contactRequests || contactRequests.length === 0) {
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Contact Requests";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Contact Requests");

      // Define columns + widths
      worksheet.columns = [
        { header: "S No", key: "sno", width: 8 },
        { header: "Full Name", key: "fullName", width: 22 },
        { header: "First Name", key: "firstName", width: 18 },
        { header: "Last Name", key: "lastName", width: 18 },
        { header: "Email Address", key: "email", width: 32 },
        { header: "Phone Number", key: "phone", width: 18 },
        { header: "Contact Message", key: "message", width: 55 },
        { header: "Active Status (Read / Unread)", key: "status", width: 28 },
        { header: "Created Date", key: "createdAt", width: 20 },
        { header: "Created By", key: "createdBy", width: 28 },
      ];

      // ── Style header row (bold + background + border + center) ──
      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.eachCell((cell) => {
        cell.font = {
          bold: true,
          size: 11,
          color: { argb: "FF000000" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF999999" } },
          left: { style: "thin", color: { argb: "FF999999" } },
          bottom: { style: "thin", color: { argb: "FF999999" } },
          right: { style: "thin", color: { argb: "FF999999" } },
        };
      });

      // ── Add data rows from CURRENT page only ──
      contactRequests.forEach((item, index) => {
        const fullName = [item.first_name, item.last_name]
          .filter(Boolean)
          .join(" ");
        worksheet.addRow({
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          fullName: fullName || "",
          firstName: item.first_name || "",
          lastName: item.last_name || "",
          email: item.email || "",
          phone: item.phone_number || "",
          message: item.message || "",
          status: Number(item.isActive) === 1 ? "Read" : "Unread",
          createdAt: new Date(item.created_at).toLocaleDateString("en-GB"),
          createdBy: item.created_by || "",
        });
      });

      // ── Style data rows (border + alignment) ──
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // skip header (already styled)
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: colNumber === 1 ? "center" : "left",
            wrapText: true,
          };
          cell.font = { size: 10 };
        });
      });

      // ── Generate and download ──
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `CONTACT_REQUEST_LIST_Page_${pagination.pageIndex + 1}.xlsx`,
      );
    } catch (err) {
      console.error("Error generating Excel:", err);
    }
  };

  // ─── Columns: S.No → Name → Email → Phone → Created Date → Status → View ───
  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorFn: (row) =>
          [row.first_name, row.last_name].filter(Boolean).join(" "),
        id: "name",
        header: "Name",
        size: 200,
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "Search Name",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 240,
        enableSorting: true,
        enableColumnFilter: false, // no search
      },
      {
        accessorKey: "phone_number",
        header: "Phone Number",
        size: 180,
        enableSorting: true,
        enableColumnFilter: false, // no search
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: "Created Date",
        size: 220,
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => <DateRangeFilter column={column} />,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v ? dayjs(v).format("DD/MM/YYYY") : "—";
        },
      },
      {
        accessorKey: "isActive",
        id: "status",
        header: "Status",
        size: 120,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: ["Read", "Unread"],
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          const isRead = value === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isRead ? "#E8F5E9" : "#FFF3E0",
                color: isRead ? "#2E7D32" : "#E65100",
                border: `1px solid ${isRead ? "#A5D6A7" : "#FFCC80"}`,
              }}
            >
              {isRead ? "Read" : "Unread"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "View",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Tooltip title="View Details" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleView(row.original);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleView, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className={styles.container}>
      {/* Header — title, bell icon, Excel download */}
      <div className={styles.header}>
        <h1 className={styles.title}>CONTACT REQUESTS</h1>

        <div
          className={styles.headerActions}
          style={{ display: "flex", alignItems: "center", gap: "12px" }}
        >
          {/* 🔔 Bell icon with active_count badge */}
          <Tooltip
            title={
              activeCount > 0
                ? `${activeCount} unread contact request${activeCount > 1 ? "s" : ""}`
                : "No unread requests"
            }
            arrow
          >
            <IconButton size="medium" aria-label="Unread notifications">
              <Badge
                badgeContent={activeCount}
                color="error"
                max={99}
                overlap="circular"
              >
                <NotificationsIcon style={{ color: "#333" }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Excel download */}
          <Tooltip title="Download Excel" arrow>
            <IconButton
              size="medium"
              onClick={handleExcelDownload}
              className={styles.menuButton}
              aria-label="Download Excel"
              disabled={loading || contactRequests.length === 0}
            >
              <i
                className="fa-solid fa-download"
                style={{ fontSize: "18px", color: "#333" }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={fetchContactRequests} className={styles.retryButton}>
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableSection}>
        <CustomMRTTable
          columns={columns}
          data={contactRequests}
          loading={loading}
          // server-side pagination & filtering
          manualPagination={true}
          manualFiltering={true}
          rowCount={totalRecords}
          pagination={pagination}
          onPaginationChange={setPagination}
          // ✅ column-wise filters — text on Enter, status + date on change
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          // toggles
          enablePagination
          enableSorting
          enableColumnFilters={true}
          enableSearch={false}
          enableColumnVisibility
          enableDensityToggle
          enableFullScreenToggle
          enableHiding
          enableStickyHeader
        />
      </div>
    </div>
  );
};

export default RequestContact;
